import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { QaroniIntermediate } from '@qaroni-core/types/qaroni-intermediate';
import { shareReplay, Subject } from 'rxjs';
import { Account, UserDTO } from '../types/account';
import { AccountApiObject } from '../types/account.response';
import { PasswordDTO } from '../types/change-password';
import { AccountErrorsService } from './account-errors.service';
import { AccountHttpService } from './account-http.service';
import { AccountSnackbarsService } from './account-snackbars.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService extends QaroniIntermediate {
  private readonly accountSubject = new Subject<Account | null>();

  private http = inject(AccountHttpService);
  private snackbars = inject(AccountSnackbarsService);
  private accountErrors = inject(AccountErrorsService);

  get account$() {
    return this.accountSubject.asObservable().pipe(shareReplay(1));
  }

  public account(): void {
    if (!(this.isAuth && this.storage.get())) {
      return;
    }

    const userID = this.storage.userID;
    this.http.account$(userID).subscribe({
      next: this.nextAccount,
      error: this.errorAccount,
    });
  }

  private nextAccount = (response: AccountApiObject): void => {
    if (!response.result.length) {
      return;
    }

    const user = response.result[0];
    this.accountSubject.next(user);
  };

  private errorAccount = (): void => {
    this.accountSubject.next(null);
  };

  public changePassword(passwordDTO: PasswordDTO): void {
    if (!(this.isAuth && this.storage.get())) {
      return;
    }

    const userID = this.storage.userID;
    this.http.changePassword$(userID, passwordDTO).subscribe({
      next: this.nextChangePassword,
      error: this.errorChangePassword,
    });
  }

  private nextChangePassword = (response: AccountApiObject): void => {
    if (!response.result[0]) {
      this.accountSubject.next(null);
      this.snackbars.failChangePassword();
      return;
    }

    this.messageSubject.next('Reset password');
    this.snackbars.successChangePassword();
  };

  private errorChangePassword = (error: HttpErrorResponse): void => {
    this.messageSubject.next('');
    this.accountErrors.passwordError(error);
  };

  public accountUpdate(userDTO: UserDTO): void {
    if (!(this.isAuth && this.storage.get())) {
      return;
    }

    const userID = this.storage.userID;
    this.http.accountUpdate$(userID, userDTO).subscribe({
      next: this.nextAccountUpdate,
      error: this.errorAccountUpdate,
    });
  }

  private nextAccountUpdate = (response: AccountApiObject): void => {
    if (!response.result[0]) {
      this.accountSubject.next(null);
      this.snackbars.failureUpdateAccount();
      return;
    }

    const account = response.result[0];
    this.accountSubject.next(account);
    this.snackbars.successUpdateAccount();
  };

  private errorAccountUpdate = (): void => {
    this.accountSubject.next(null);
    this.snackbars.failureUpdateAccount();
  };

  public deleteImage(userID: number): void {
    if (!(this.isAuth && this.storage.get())) {
      return;
    }

    this.http.deleteImage$(userID).subscribe({
      next: this.nextDeleteImage,
      error: this.errorDeleteImage,
    });
  }

  private nextDeleteImage = (): void => {
    this.snackbars.successUpdateImage();
    this.messageSubject.next('Delete done');
  };

  private errorDeleteImage = (): void => {
    this.snackbars.failChangePassword();
    this.messageSubject.next('');
  };
}
